/**
 * Scrubbing for Grafana Faro payloads (runs in `beforeSend`, before transport).
 *
 * Faro events land in Loki as plain text. Upload part URLs look like
 * `/s3-upload/mpu_<ts>_<hex>/<part>?s3UploadId=…&token=<guest token>&s3Key=…`,
 * and `faro.performance.resource` records every such URL in `event_data_name`.
 * Anything URL-shaped therefore loses its query string and fragment, and path
 * segments that look like credentials or opaque ids are replaced — in every
 * string of the payload (resource timings, page meta, CSP reports, error
 * messages and stack frames), not only in fields whose key says "url".
 */

export const REDACTED = "[redacted]";

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const BEARER_RE = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
const JWT_RE = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const SENSITIVE_KEY_RE = /(token|jwt|secret|password|authorization|email|phone|audio|transcript|text|content|message|utm)/i;
const URL_KEY_RE = /(url|href|path|route|request|endpoint|uri|referrer|referer)/i;
/** Absolute URLs embedded anywhere in a string (messages, stacks, resource names). */
const EMBEDDED_URL_RE = /\b(?:https?|wss?):\/\/[^\s"'<>()`\\]+/gi;
/** Root-relative paths that carry a query string, e.g. `PUT /s3-upload/…?token=…`. */
const EMBEDDED_PATH_WITH_QUERY_RE = /(^|[\s"'(=:,])(\/[^\s"'<>()`\\?#]*[?#][^\s"'<>()`\\]*)/g;
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:\/\//i;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LONG_HEX_RUN_RE = /[0-9a-f]{16,}/i;
const OPAQUE_TOKEN_RE = /^[A-Za-z0-9_~-]{20,}$/;
/** Build assets keep their paths: Alloy maps stack frames to source maps by URL. */
const STATIC_PATH_PREFIXES = ["/_next/", "/app/assets/", "/assets/", "/static/"];

function isTokenLikeSegment(segment: string): boolean {
  let value = segment;
  try {
    value = decodeURIComponent(segment);
  } catch {
    // keep the raw segment
  }
  if (!value) return false;
  if (value.startsWith("eyJ")) return true; // JWT, even with its dots
  if (UUID_RE.test(value)) return true;
  if (value.includes(".")) return false; // file names like chunk-<hash>.js
  if (LONG_HEX_RUN_RE.test(value)) return true; // mpu_<ts>_<hex>, share tokens, hex ids
  return OPAQUE_TOKEN_RE.test(value) && /\d/.test(value) && /[a-z]/.test(value) && /[A-Z]/.test(value);
}

export function scrubPath(pathname: string): string {
  if (STATIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return pathname;
  return pathname
    .split("/")
    .map((segment) => (isTokenLikeSegment(segment) ? REDACTED : segment))
    .join("/");
}

function currentOrigin(): string {
  return typeof window !== "undefined" && window.location?.origin ? window.location.origin : "http://localhost";
}

function looksLikeUrl(value: string): boolean {
  return SCHEME_RE.test(value) || value.startsWith("/");
}

/** Drops query string and fragment and redacts token-like path segments. */
export function scrubUrl(value: string): string {
  const relative = !SCHEME_RE.test(value);
  try {
    const url = new URL(value, currentOrigin());
    const path = scrubPath(url.pathname);
    if (relative) return path;
    // `origin` is the string "null" for chrome-extension:, blob: and similar.
    const prefix = url.origin !== "null" ? url.origin : `${url.protocol}//${url.host}`;
    return `${prefix}${path}`;
  } catch {
    const withoutQuery = value.replace(/[?#].*$/, "");
    const schemeMatch = withoutQuery.match(/^([a-z][a-z0-9+.-]*:\/\/[^/]*)(.*)$/i);
    return schemeMatch ? `${schemeMatch[1]}${scrubPath(schemeMatch[2])}` : scrubPath(withoutQuery);
  }
}

export function scrubString(value: string): string {
  const masked = value
    .replace(BEARER_RE, `Bearer ${REDACTED}`)
    .replace(JWT_RE, REDACTED)
    .replace(EMAIL_RE, REDACTED)
    .replace(EMBEDDED_URL_RE, (match) => scrubUrl(match))
    .replace(EMBEDDED_PATH_WITH_QUERY_RE, (_match, lead: string, path: string) => `${lead}${scrubUrl(path)}`);
  return masked.length > 2048 ? `${masked.slice(0, 2048)}...` : masked;
}

export function scrubPayload(value: unknown, key = "", depth = 0): unknown {
  if (value == null || depth > 8) return value;
  if (typeof value === "string") {
    if (SENSITIVE_KEY_RE.test(key)) return REDACTED;
    if (URL_KEY_RE.test(key) && looksLikeUrl(value)) return scrubUrl(scrubString(value));
    return scrubString(value);
  }
  if (typeof value !== "object") return value;
  if (Array.isArray(value)) return value.slice(0, 50).map((item) => scrubPayload(item, key, depth + 1));
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([childKey, childValue]) => [
      childKey,
      scrubPayload(childValue, childKey, depth + 1),
    ]),
  );
}

/**
 * `beforeSend` hook body. On top of the generic walk, the page id — Faro sets it
 * to `location.pathname`, which is not URL-keyed and has no scheme — gets the
 * path treatment too.
 */
export function scrubFaroItem<T>(item: T): T {
  const scrubbed = scrubPayload(item) as T & { meta?: { page?: { id?: unknown; url?: unknown } } };
  const page = scrubbed?.meta?.page;
  if (page && typeof page.id === "string" && page.id.startsWith("/")) page.id = scrubUrl(page.id);
  return scrubbed;
}
